import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import {
  UploadSimple, CheckCircle, WarningCircle, CircleNotch, MagnifyingGlass,
  FileArrowUp, GraduationCap, FileText, ShieldCheck, Trash, PaperPlaneTilt,
} from '@phosphor-icons/react';
import PreAuthorizationModal from '../PreAuthorizationModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyPreview = { total_rows: 0, matched: [], review: [], not_found: [] };

const useListUpload = (kind) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [checkedMatched, setCheckedMatched] = useState({});
  const [checkedReview, setCheckedReview] = useState({});

  const reset = useCallback(() => {
    setFile(null);
    setPreview(null);
    setCheckedMatched({});
    setCheckedReview({});
  }, []);

  const upload = useCallback(async () => {
    if (!file) return;
    setLoading(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/preauth/${kind}-list/preview`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
      const m = {};
      (res.data?.matched || []).forEach(r => { if (r.student_id) m[r.student_id] = true; });
      setCheckedMatched(m);
      setCheckedReview({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not read the Excel file');
    } finally {
      setLoading(false);
    }
  }, [file, kind]);

  const apply = useCallback(async (onDone) => {
    const ids = new Set();
    (preview?.matched || []).forEach(r => {
      if (r.student_id && checkedMatched[r.student_id]) ids.add(r.student_id);
    });
    (preview?.review || []).forEach(r => {
      if (r.student_id && checkedReview[r.student_id]) ids.add(r.student_id);
    });
    const idList = Array.from(ids);
    if (!window.confirm(`Replace the ${kind === 'coe' ? 'COE Received' : 'EA Signed'} list with ${idList.length} student${idList.length === 1 ? '' : 's'}? The current list will be cleared first.`)) return;
    setApplying(true);
    try {
      const res = await axios.post(`${API}/admin/preauth/${kind}-list/replace`, { ids: idList });
      toast.success(`${kind === 'coe' ? 'COE' : 'EA Signed'} list replaced — ${res.data?.stamped ?? 0} student${res.data?.stamped === 1 ? '' : 's'} stamped.`);
      setOpen(false);
      reset();
      onDone && onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to apply list');
    } finally {
      setApplying(false);
    }
  }, [preview, checkedMatched, checkedReview, kind, reset]);

  return {
    open, setOpen,
    file, setFile,
    preview, setPreview,
    loading, applying,
    checkedMatched, setCheckedMatched,
    checkedReview, setCheckedReview,
    reset, upload, apply,
  };
};

const ListUploadDialog = ({ hook, kind, title, color }) => {
  const kindLabel = kind === 'coe' ? 'COE Received' : 'EA Signed';
  const colorClasses = color === 'blue'
    ? { button: 'bg-blue-700 hover:bg-blue-800', border: 'border-blue-200', text: 'text-blue-800', bg: 'bg-blue-50', fileBorder: 'border-blue-200', fileBg: 'bg-blue-100', fileHover: 'hover:bg-blue-200', fileText: 'text-blue-800' }
    : { button: 'bg-emerald-700 hover:bg-emerald-800', border: 'border-emerald-200', text: 'text-emerald-800', bg: 'bg-emerald-50', fileBorder: 'border-emerald-200', fileBg: 'bg-emerald-100', fileHover: 'hover:bg-emerald-200', fileText: 'text-emerald-800' };

  return (
    <Dialog open={hook.open} onOpenChange={(o) => { hook.setOpen(o); if (!o) hook.reset(); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid={`preauth-${kind}-dialog`}>
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }} className={`flex items-center gap-2 ${colorClasses.text}`}>
            {kind === 'coe' ? <GraduationCap size={20} weight="duotone" /> : <FileText size={20} weight="duotone" />} {title}
          </DialogTitle>
          <DialogDescription>
            Upload a sheet. Just a <code>Name</code> column works, or <code>Name + Email</code>, or DocuSign-style headers. This list will <strong>replace</strong> the current {kindLabel} list — students removed from the sheet lose their stamp.
          </DialogDescription>
        </DialogHeader>

        {!hook.preview ? (
          <div className="space-y-4">
            <div className={`border-2 border-dashed ${colorClasses.fileBorder} rounded-lg p-6 text-center bg-gray-50`}>
              <input
                type="file"
                accept=".xlsx,.xlsm"
                onChange={(e) => hook.setFile(e.target.files?.[0] || null)}
                className={`block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:${colorClasses.fileBg} file:${colorClasses.fileText} file:${colorClasses.fileHover}`}
                data-testid={`preauth-${kind}-file-input`}
              />
              {hook.file && <p className="text-xs text-gray-500 mt-2">Selected: {hook.file.name}</p>}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { hook.setOpen(false); hook.reset(); }}>Cancel</Button>
              <Button onClick={hook.upload} disabled={!hook.file || hook.loading} className={`${colorClasses.button} text-white`} data-testid={`preauth-${kind}-preview-btn`}>
                {hook.loading ? <><CircleNotch size={14} className="mr-1.5 animate-spin" /> Scanning…</> : <><MagnifyingGlass size={14} className="mr-1.5" /> Preview matches</>}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-3">
              <div className="border rounded-lg p-3 bg-gray-50"><p className="text-[10px] uppercase font-semibold text-gray-500">Total rows</p><p className="text-xl font-semibold">{hook.preview.total_rows}</p></div>
              <div className="border rounded-lg p-3 bg-emerald-50 border-emerald-200"><p className="text-[10px] uppercase font-semibold text-emerald-700">Confident</p><p className="text-xl font-semibold text-emerald-800">{hook.preview.matched.length}</p></div>
              <div className="border rounded-lg p-3 bg-amber-50 border-amber-200"><p className="text-[10px] uppercase font-semibold text-amber-700">Review</p><p className="text-xl font-semibold text-amber-800">{hook.preview.review.length}</p></div>
              <div className="border rounded-lg p-3 bg-red-50 border-red-200"><p className="text-[10px] uppercase font-semibold text-red-700">Not found</p><p className="text-xl font-semibold text-red-800">{hook.preview.not_found.length}</p></div>
            </div>

            {hook.preview.matched.length > 0 && (
              <div className="border border-emerald-200 rounded-lg overflow-hidden">
                <div className="bg-emerald-50 px-4 py-2 flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-emerald-900">Confident matches ({hook.preview.matched.length})</h4>
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-emerald-800" onClick={() => {
                    const allChecked = hook.preview.matched.every(r => hook.checkedMatched[r.student_id]);
                    const m = {};
                    if (!allChecked) hook.preview.matched.forEach(r => { if (r.student_id) m[r.student_id] = true; });
                    hook.setCheckedMatched(m);
                  }}>Toggle all</Button>
                </div>
                <div className="max-h-[260px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0"><tr><th className="w-8 px-3 py-2"></th><th className="text-left px-3 py-2">Student</th><th className="text-left px-3 py-2">Email</th><th className="text-left px-3 py-2">Recruiter</th></tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {hook.preview.matched.map(r => (
                        <tr key={r.student_id || r.row}>
                          <td className="px-3 py-2"><Checkbox checked={!!hook.checkedMatched[r.student_id]} onCheckedChange={(v) => hook.setCheckedMatched(prev => ({ ...prev, [r.student_id]: !!v }))} /></td>
                          <td className="px-3 py-2 text-gray-900">{r.student_full_name}</td>
                          <td className="px-3 py-2 text-gray-600">{r.student_email}</td>
                          <td className="px-3 py-2 text-gray-600">{r.recruiter_name || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {hook.preview.review.length > 0 && (
              <div className="border border-amber-200 rounded-lg overflow-hidden">
                <div className="bg-amber-50 px-4 py-2"><h4 className="text-sm font-semibold text-amber-900">Needs review ({hook.preview.review.length})</h4><p className="text-[11px] text-amber-800 mt-0.5">Email matched but name is unusual, or shared email. Tick the ones that look right.</p></div>
                <div className="max-h-[220px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0"><tr><th className="w-8 px-3 py-2"></th><th className="text-left px-3 py-2">Sheet row</th><th className="text-left px-3 py-2">Matched student</th><th className="text-left px-3 py-2">Reason</th></tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {hook.preview.review.map(r => (
                        <tr key={r.student_id || r.row}>
                          <td className="px-3 py-2"><Checkbox checked={!!hook.checkedReview[r.student_id]} onCheckedChange={(v) => hook.setCheckedReview(prev => ({ ...prev, [r.student_id]: !!v }))} disabled={!r.student_id} /></td>
                          <td className="px-3 py-2 text-gray-900"><div>{r.sheet_first_middle} {r.sheet_last_name}</div><div className="text-[10.5px] text-gray-500">{r.sheet_email}</div></td>
                          <td className="px-3 py-2 text-gray-900">{r.student_id ? <><div>{r.student_full_name}</div><div className="text-[10.5px] text-gray-500">{r.student_email}</div></> : <span className="text-gray-400">—</span>}</td>
                          <td className="px-3 py-2 text-amber-900">{r.match_reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {hook.preview.not_found.length > 0 && (
              <div className="border border-red-200 rounded-lg overflow-hidden">
                <div className="bg-red-50 px-4 py-2"><h4 className="text-sm font-semibold text-red-900">Not found ({hook.preview.not_found.length})</h4><p className="text-[11px] text-red-800 mt-0.5">These sheet rows didn't match anyone in the system. They are skipped.</p></div>
                <div className="max-h-[160px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0"><tr><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Email</th></tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {hook.preview.not_found.map(r => (
                        <tr key={r.row}><td className="px-3 py-2 text-gray-900">{r.sheet_first_middle} {r.sheet_last_name}</td><td className="px-3 py-2 text-gray-600">{r.sheet_email}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => hook.reset()}>Upload another file</Button>
              <Button onClick={() => hook.apply()} disabled={hook.applying} className={`${colorClasses.button} text-white`} data-testid={`preauth-${kind}-apply-btn`}>
                {hook.applying ? <><CircleNotch size={14} className="mr-1.5 animate-spin" /> Replacing…</> : <><CheckCircle size={14} className="mr-1.5" /> Replace {kindLabel} list</>}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const PreAuthorizationPanel = () => {
  const [summary, setSummary] = useState({ coe_count: 0, ea_count: 0, eligible_count: 0, latest_coe_upload: null, latest_ea_upload: null });
  const [eligible, setEligible] = useState([]);
  const [search, setSearch] = useState('');
  const [recruiterFilter, setRecruiterFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('coe_only'); // coe_only (default) | ready | sent | self_completed | all
  const [loading, setLoading] = useState(false);
  const [preAuthStudent, setPreAuthStudent] = useState(null);
  const coeHook = useListUpload('coe');
  const eaHook = useListUpload('ea');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, listRes] = await Promise.all([
        axios.get(`${API}/admin/preauth/summary`),
        axios.get(`${API}/admin/preauth/eligible`, { params: { search } }),
      ]);
      setSummary(sumRes.data || {});
      setEligible(listRes.data?.students || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load Pre-Auth data');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const recruiterOptions = React.useMemo(() => {
    const set = new Map();
    (eligible || []).forEach(s => {
      const nm = s.recruiter_name || '— no recruiter —';
      if (!set.has(nm)) set.set(nm, 0);
      set.set(nm, set.get(nm) + 1);
    });
    return Array.from(set.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [eligible]);

  const statusOf = (s) => {
    if (s.pre_auth_self_completed_at) return 'self_completed';
    if (s.pre_auth_sent_at) return 'sent';
    if (!s.ea_signed) return 'coe_only';
    return 'ready';
  };

  const filtered = React.useMemo(() => {
    return (eligible || []).filter(s => {
      if (recruiterFilter !== 'all') {
        const nm = s.recruiter_name || '— no recruiter —';
        if (nm !== recruiterFilter) return false;
      }
      if (statusFilter !== 'all' && statusOf(s) !== statusFilter) return false;
      return true;
    });
  }, [eligible, recruiterFilter, statusFilter]);

  const statusCounts = React.useMemo(() => {
    const c = { coe_only: 0, ready: 0, sent: 0, self_completed: 0 };
    (eligible || []).forEach(s => { c[statusOf(s)]++; });
    return c;
  }, [eligible]);

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

  const clearFilters = () => { setRecruiterFilter('all'); setStatusFilter('coe_only'); setSearch(''); };
  const activeFilterCount = (recruiterFilter !== 'all' ? 1 : 0) + (statusFilter !== 'coe_only' ? 1 : 0) + (search ? 1 : 0);

  const [downloadingAll, setDownloadingAll] = useState(false);
  const [markingDoneId, setMarkingDoneId] = useState(null);
  const selfCompletedCount = Number(summary.self_completed_count || 0);

  const handleMarkDone = useCallback(async (student) => {
    if (!student) return;
    const ok = window.confirm(
      `Mark Pre-Authorization as DONE for ${student.full_name}?\n\nUse this only when the pre-auth was handled manually off the portal. It will show as Self-completed in all reports. You can undo it later.`
    );
    if (!ok) return;
    setMarkingDoneId(student.id);
    try {
      await axios.post(`${API}/admin/preauth/students/${student.id}/mark-done`);
      toast.success(`Marked Pre-Auth done for ${student.full_name}`);
      await fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not mark Pre-Auth done');
    } finally {
      setMarkingDoneId(null);
    }
  }, []);

  const handleUndoMarkDone = useCallback(async (student) => {
    if (!student) return;
    const ok = window.confirm(
      `Undo the manual Pre-Auth mark for ${student.full_name}?\n\nThe student will go back to "Ready to send" (Pre-Auth status cleared).`
    );
    if (!ok) return;
    setMarkingDoneId(student.id);
    try {
      await axios.post(`${API}/admin/preauth/students/${student.id}/undo-mark-done`);
      toast.success(`Undone — ${student.full_name} is back to Ready to send`);
      await fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not undo the mark');
    } finally {
      setMarkingDoneId(null);
    }
  }, []);
  const [backfillInfo, setBackfillInfo] = useState({ total_self_completed: 0, pending_backfill: 0, already_backfilled: 0 });
  const [backfilling, setBackfilling] = useState(false);

  const loadBackfillPreview = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/preauth/self-pdf-backfill/preview`);
      setBackfillInfo({
        total_self_completed: Number(data?.total_self_completed || 0),
        pending_backfill: Number(data?.pending_backfill || 0),
        already_backfilled: Number(data?.already_backfilled || 0),
      });
    } catch (_) { /* non-blocking */ }
  }, []);

  useEffect(() => { loadBackfillPreview(); }, [loadBackfillPreview]);

  const handleBackfill = useCallback(async () => {
    if (!backfillInfo.pending_backfill) return;
    const n = backfillInfo.pending_backfill;
    if (!window.confirm(
      `Fix missing student info on ${n} existing signed PDF${n === 1 ? '' : 's'}?\n\nThis adds the student's name, program, address, phone, email, and the Bank Authorization checkbox to each PDF without touching the signature. Safe to re-run.`
    )) return;
    setBackfilling(true);
    try {
      const { data } = await axios.post(`${API}/admin/preauth/self-pdf-backfill`);
      const processed = Number(data?.processed || 0);
      const skipped = Number(data?.skipped_already_backfilled || 0);
      const failures = Array.isArray(data?.failures) ? data.failures.length : 0;
      toast.success(
        failures
          ? `Fixed ${processed} PDF${processed === 1 ? '' : 's'} — ${failures} failed, ${skipped} already done`
          : `Fixed ${processed} PDF${processed === 1 ? '' : 's'} (${skipped} were already up to date)`
      );
      await loadBackfillPreview();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Backfill failed');
    } finally {
      setBackfilling(false);
    }
  }, [backfillInfo, loadBackfillPreview]);
  const handleDownloadAll = useCallback(async () => {
    if (!selfCompletedCount) return;
    setDownloadingAll(true);
    try {
      const res = await axios.get(`${API}/admin/preauth/download-all`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const stamp = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 13);
      const a = document.createElement('a');
      a.href = url;
      a.download = `preauth_signed_pdfs_admin_${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const n = res.headers['x-preauth-pdf-count'] || selfCompletedCount;
      toast.success(`Downloaded ${n} Pre-Auth PDF${String(n) === '1' ? '' : 's'}`);
    } catch (err) {
      let msg = 'Could not build the ZIP download';
      if (err?.response?.data) {
        try {
          const text = await err.response.data.text();
          const j = JSON.parse(text);
          msg = j?.detail || msg;
        } catch (_) { /* ignore */ }
      }
      toast.error(msg);
    } finally {
      setDownloadingAll(false);
    }
  }, [selfCompletedCount]);

  return (
    <div className="space-y-6" data-testid="preauth-panel">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-indigo-100 flex items-center justify-center">
          <ShieldCheck size={22} weight="duotone" className="text-indigo-700" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-gray-900" style={{ fontFamily: "'Libre Baskerville', serif" }}>Pre-Authorization</h2>
          <p className="text-xs text-gray-500">Upload the two source lists. Students appearing in both become eligible to receive the Pre-Auth payment link on their card.</p>
        </div>
      </div>

      {/* ---- the two upload cards ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* COE */}
        <div className="border border-emerald-200 rounded-xl bg-white overflow-hidden shadow-sm" data-testid="preauth-coe-card">
          <div className="flex items-start gap-3 p-5">
            <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <GraduationCap size={20} weight="duotone" className="text-emerald-700" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">COE Received list</h3>
              <p className="text-xs text-gray-500 mt-0.5">Includes students at the <strong>COE Sent pipeline stage</strong> (auto-detected) and anyone added via the upload below.</p>
              <div className="mt-3 flex items-center gap-3 text-sm flex-wrap">
                <span className="inline-flex items-center gap-1 text-emerald-800 font-semibold"><CheckCircle size={14} weight="fill" /> {summary.coe_count} student{summary.coe_count === 1 ? '' : 's'} total</span>
                <span className="text-[11px] text-gray-500">
                  {(summary.coe_from_pipeline || 0)} from pipeline · {(summary.coe_from_upload || 0)} from upload
                </span>
              </div>
              {summary.latest_coe_upload && (
                <p className="text-[11px] text-gray-500 mt-1">Last upload {fmtDate(summary.latest_coe_upload?.uploaded_at)} by {summary.latest_coe_upload?.uploaded_by}</p>
              )}
            </div>
          </div>
          <div className="px-5 pb-5">
            <Button onClick={() => coeHook.setOpen(true)} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="preauth-coe-upload-btn">
              <UploadSimple size={14} className="mr-1.5" /> Upload COE list
            </Button>
          </div>
        </div>

        {/* EA */}
        <div className="border border-blue-200 rounded-xl bg-white overflow-hidden shadow-sm" data-testid="preauth-ea-card">
          <div className="flex items-start gap-3 p-5">
            <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <FileText size={20} weight="duotone" className="text-blue-700" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">EA Signed list</h3>
              <p className="text-xs text-gray-500 mt-0.5">Includes students at <strong>EA Signed pipeline stage</strong> and anyone with a DocuSign signed stamp (auto-detected) plus the upload below.</p>
              <div className="mt-3 flex items-center gap-3 text-sm flex-wrap">
                <span className="inline-flex items-center gap-1 text-blue-800 font-semibold"><CheckCircle size={14} weight="fill" /> {summary.ea_count} student{summary.ea_count === 1 ? '' : 's'} total</span>
                <span className="text-[11px] text-gray-500">
                  {(summary.ea_from_pipeline || 0)} from pipeline · {(summary.ea_from_upload || 0)} from upload
                </span>
              </div>
              {summary.latest_ea_upload && (
                <p className="text-[11px] text-gray-500 mt-1">Last upload {fmtDate(summary.latest_ea_upload?.uploaded_at)} by {summary.latest_ea_upload?.uploaded_by}</p>
              )}
            </div>
          </div>
          <div className="px-5 pb-5">
            <Button onClick={() => eaHook.setOpen(true)} className="w-full bg-blue-700 hover:bg-blue-800 text-white" data-testid="preauth-ea-upload-btn">
              <UploadSimple size={14} className="mr-1.5" /> Upload EA Signed list
            </Button>
          </div>
        </div>
      </div>

      {/* ---- eligible intersection card ---- */}
      <div className="border border-indigo-200 rounded-xl bg-white shadow-sm" data-testid="preauth-eligible-card">
        <div className="flex items-center justify-between gap-3 p-5 border-b border-indigo-100 bg-indigo-50/60">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-indigo-200/70 flex items-center justify-center">
              <ShieldCheck size={20} weight="duotone" className="text-indigo-800" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Pre-Auth list <span className="text-gray-500 font-normal">({filtered.length}{filtered.length !== (eligible || []).length ? ` of ${(eligible || []).length}` : ''})</span></h3>
              <p className="text-[11px] text-gray-600">Every student with COE Received. Those with EA Signed also show the Pre-Auth action; the rest show "COE received · EA not signed".</p>
            </div>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="w-64">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email, recruiter…"
                className="h-9 text-xs"
                data-testid="preauth-eligible-search"
              />
            </div>
            <Button
              type="button"
              onClick={handleBackfill}
              disabled={!backfillInfo.pending_backfill || backfilling}
              variant="outline"
              className="h-9 text-xs border-amber-300 text-amber-800 hover:bg-amber-50 disabled:opacity-50"
              data-testid="preauth-backfill-btn"
              title={backfillInfo.pending_backfill
                ? `Add missing name/program/address/phone/email to ${backfillInfo.pending_backfill} existing signed PDF${backfillInfo.pending_backfill === 1 ? '' : 's'}`
                : 'All existing signed PDFs are already up to date'}
            >
              {backfilling
                ? (<><CircleNotch size={13} className="mr-1.5 animate-spin" /> Fixing…</>)
                : (<><WarningCircle size={13} weight="bold" className="mr-1.5" /> Fix old PDFs ({backfillInfo.pending_backfill})</>)}
            </Button>
            <Button
              type="button"
              onClick={handleDownloadAll}
              disabled={!selfCompletedCount || downloadingAll}
              className="h-9 text-xs bg-emerald-700 hover:bg-emerald-800 text-white disabled:opacity-50"
              data-testid="preauth-download-all-btn"
              title={selfCompletedCount
                ? `Download all ${selfCompletedCount} self-completed Pre-Auth PDF${selfCompletedCount === 1 ? '' : 's'} as a ZIP`
                : 'No self-completed PDFs available yet'}
            >
              {downloadingAll
                ? (<><CircleNotch size={13} className="mr-1.5 animate-spin" /> Preparing…</>)
                : (<><FileArrowUp size={13} weight="bold" className="mr-1.5" /> Download All ({selfCompletedCount})</>)}
            </Button>
          </div>
        </div>

        {/* Filter bar */}
        <div className="px-5 py-3 border-b border-indigo-100 bg-white flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-[10px] uppercase font-semibold text-gray-500 tracking-wide">Recruiter</label>
            <select
              value={recruiterFilter}
              onChange={(e) => setRecruiterFilter(e.target.value)}
              className="h-8 text-xs border border-gray-200 rounded-md px-2 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-[180px]"
              data-testid="preauth-filter-recruiter"
            >
              <option value="all">All recruiters</option>
              {recruiterOptions.map(([name, count]) => (
                <option key={name} value={name}>{name} ({count})</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[10px] uppercase font-semibold text-gray-500 tracking-wide">Status</label>
            <div className="flex items-center gap-1" role="group" data-testid="preauth-filter-status">
              {[
                { key: 'coe_only', label: 'COE only', count: statusCounts.coe_only, cls: 'bg-white text-amber-800 border-amber-200 hover:bg-amber-50', activeCls: 'bg-amber-600 text-white border-amber-600' },
                { key: 'ready', label: 'Ready to send', count: statusCounts.ready, cls: 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50', activeCls: 'bg-gray-900 text-white border-gray-900' },
                { key: 'sent', label: 'Email sent', count: statusCounts.sent, cls: 'bg-white text-blue-800 border-blue-200 hover:bg-blue-50', activeCls: 'bg-blue-700 text-white border-blue-700' },
                { key: 'self_completed', label: 'Self-completed', count: statusCounts.self_completed, cls: 'bg-white text-emerald-800 border-emerald-200 hover:bg-emerald-50', activeCls: 'bg-emerald-700 text-white border-emerald-700' },
                { key: 'all', label: 'All', count: eligible.length, cls: 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50', activeCls: 'bg-gray-900 text-white border-gray-900' },
              ].map(opt => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setStatusFilter(opt.key)}
                  className={`inline-flex items-center gap-1 px-2.5 h-8 text-[11px] font-semibold rounded-md border transition-colors ${statusFilter === opt.key ? opt.activeCls : opt.cls}`}
                  data-testid={`preauth-filter-status-${opt.key}`}
                >
                  {opt.label} <span className={`text-[9.5px] font-normal ${statusFilter === opt.key ? 'opacity-90' : 'opacity-60'}`}>({opt.count})</span>
                </button>
              ))}
            </div>
          </div>
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={clearFilters}
              className="ml-auto text-[11px] font-medium text-indigo-700 hover:text-indigo-900 hover:underline"
              data-testid="preauth-filter-clear"
            >
              Clear filters ({activeFilterCount})
            </button>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-2 font-semibold text-gray-700">Student</th>
                <th className="text-left px-4 py-2 font-semibold text-gray-700">Recruiter</th>
                <th className="text-left px-4 py-2 font-semibold text-gray-700">College / Program</th>
                <th className="text-left px-4 py-2 font-semibold text-gray-700">Sale price</th>
                <th className="text-left px-4 py-2 font-semibold text-gray-700">Pre-Auth status</th>
                <th className="text-right px-4 py-2 font-semibold text-gray-700">Signed PDF</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500"><CircleNotch size={16} className="animate-spin inline mr-2" /> Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                  {eligible.length === 0
                    ? 'No students with COE Received yet. Upload the COE sheet (or move students to the "COE Sent" pipeline stage) to see them here.'
                    : 'No students match the current filters.'}
                </td></tr>
              )}
              {!loading && filtered.map(s => {
                const eaSigned = !!s.ea_signed;
                const needsAction = eaSigned && !s.pre_auth_sent_at && !s.pre_auth_self_completed_at;
                return (
                <tr key={s.id} className={needsAction ? 'bg-amber-50/40' : ''} data-testid={`preauth-eligible-row-${s.id}`}>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-gray-900">{s.full_name}</div>
                    <div className="text-[10.5px] text-gray-500">{s.email}</div>
                  </td>
                  <td className="px-4 py-2.5 text-gray-700">{s.recruiter_name || '—'}</td>
                  <td className="px-4 py-2.5 text-gray-700">
                    <div>{s.assigned_college_name || '—'}</div>
                    <div className="text-[10.5px] text-gray-500">{s.assigned_program_name || ''}</div>
                  </td>
                  <td className="px-4 py-2.5 text-gray-900">{s.sale_price ? `$${Number(s.sale_price).toLocaleString()}` : '—'}</td>
                  <td className="px-4 py-2.5">
                    {s.pre_auth_self_completed_at ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200" title={s.pre_auth_manually_marked_by ? `Marked done manually by ${s.pre_auth_manually_marked_by} on ${(s.pre_auth_manually_marked_at || '').slice(0, 10)}` : `Self-completed ${(s.pre_auth_self_completed_at || '').slice(0, 10)}`}><CheckCircle size={10} weight="fill" /> {s.pre_auth_manually_marked_by ? 'Marked done · manual' : 'Self-completed'}</span>
                    ) : s.pre_auth_sent_at ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-blue-50 text-blue-800 border border-blue-200" title={`Email sent ${(s.pre_auth_sent_at || '').slice(0, 10)}`}><CheckCircle size={10} weight="fill" /> Email sent</span>
                    ) : !eaSigned ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-amber-50 text-amber-900 border border-amber-200" title="COE received but EA has not been signed yet — Pre-Auth is unlocked only after EA signature.">COE received · EA not signed</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-gray-50 text-gray-700 border border-gray-200">Ready to send</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {s.pre_auth_signed_file_id ? (
                      <a
                        href={`${API}/files/${s.pre_auth_signed_file_id}/download`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 px-2.5 h-7 text-[11px] font-semibold rounded-md bg-emerald-700 hover:bg-emerald-800 text-white"
                        data-testid={`preauth-download-${s.id}`}
                        title={`Download signed declaration PDF (completed ${(s.pre_auth_self_completed_at || '').slice(0, 10)})`}
                      >
                        <FileArrowUp size={11} weight="bold" /> Download PDF
                      </a>
                    ) : s.pre_auth_manually_marked_by ? (
                      <div className="inline-flex items-center gap-1.5">
                        <span className="inline-flex items-center gap-1 px-2 h-7 text-[10.5px] font-semibold rounded-md bg-emerald-50 text-emerald-800 border border-emerald-200" title={`Marked done manually by ${s.pre_auth_manually_marked_by} on ${(s.pre_auth_manually_marked_at || '').slice(0, 10)}`}>
                          <CheckCircle size={11} weight="fill" /> Marked done
                        </span>
                        <button
                          type="button"
                          onClick={() => handleUndoMarkDone(s)}
                          disabled={markingDoneId === s.id}
                          className="inline-flex items-center gap-1 px-2 h-7 text-[10.5px] font-semibold rounded-md bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 disabled:opacity-50"
                          data-testid={`preauth-undo-mark-${s.id}`}
                          title="Undo the manual Pre-Auth mark"
                        >
                          {markingDoneId === s.id ? <CircleNotch size={10} className="animate-spin" /> : 'Undo'}
                        </button>
                      </div>
                    ) : s.pre_auth_sent_at ? (
                      <span className="inline-flex items-center gap-1 px-2 h-7 text-[11px] font-semibold rounded-md bg-gray-100 text-gray-500 border border-gray-200" title={`Pre-Auth email sent ${(s.pre_auth_sent_at || '').slice(0, 10)}`}>
                        <PaperPlaneTilt size={11} weight="fill" /> Awaiting signature
                      </span>
                    ) : !eaSigned ? (
                      <span className="inline-flex items-center text-[10.5px] text-gray-400" title="Pre-Auth action unlocks once the EA is signed.">—</span>
                    ) : (
                      <div className="inline-flex items-center gap-1.5">
                        <Button
                          size="sm"
                          onClick={() => setPreAuthStudent(s)}
                          className="h-7 text-[11px] font-semibold bg-indigo-700 hover:bg-indigo-800 text-white"
                          data-testid={`preauth-send-${s.id}`}
                          title="Email the Pre-Authorization link to the student"
                        >
                          <PaperPlaneTilt size={11} weight="fill" className="mr-1" /> Send Pre-Auth
                        </Button>
                        <button
                          type="button"
                          onClick={() => handleMarkDone(s)}
                          disabled={markingDoneId === s.id}
                          className="inline-flex items-center gap-1 px-2 h-7 text-[10.5px] font-semibold rounded-md bg-white border border-emerald-300 hover:bg-emerald-50 text-emerald-800 disabled:opacity-50"
                          data-testid={`preauth-mark-done-${s.id}`}
                          title="Mark as done — use only when Pre-Auth was handled manually off-portal"
                        >
                          {markingDoneId === s.id ? <CircleNotch size={10} className="animate-spin" /> : <><CheckCircle size={10} weight="fill" /> Mark done</>}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <ListUploadDialog hook={coeHook} kind="coe" title="Upload COE Received list" color="emerald" />
      <ListUploadDialog hook={eaHook} kind="ea" title="Upload EA Signed list" color="blue" />

      <PreAuthorizationModal
        open={!!preAuthStudent}
        onClose={() => setPreAuthStudent(null)}
        student={preAuthStudent}
        onSent={({ sent_at, to }) => {
          setEligible(prev => prev.map(s => s.id === preAuthStudent?.id
            ? { ...s, pre_auth_sent_at: sent_at, pre_auth_sent_to: to }
            : s));
          fetchAll();
        }}
      />
    </div>
  );
};

export default PreAuthorizationPanel;
